import { useSelector } from 'react-redux';
import { StorageService, type AppState } from '@nextgen-web-framework/core';
import { useBreakpoint, AccountType } from '@nextgen-web-framework/all';
import {
  DynamicObjectInterface,
  RegistrationFlowDataType,
  profileApi,
  ImageReturnType,
  PersonalIdRequestTypeEnum,
} from '@commerce-profile-webcomponents/sdk';
import RegistrationIdVerificationStep from '../../RegistrationIdVerificationStep/RegistrationIdVerificationStep';
import { NextHandlerProps, APICallType } from '../../Registration/types/propTypes';
import { generateCustomHeaders } from '../../../utils/registrationUtils';
import { useGlobalErrorHandler } from '../../../utils/globalErrorHook';
import { COAPP_STEPS, SHARECODE_ATTRIBUTES } from '../../../utils/Constants';
import { parseNumber, parseObject, parseString } from '../../../utils/helper';
import { CoAppCommonProps } from '../types/propTypes';
import styles from './CoAppIdStep.module.scss';

const CoAppIdStep = ({ nextHandler, ...props }: CoAppCommonProps) => {
  const breakpoint = useBreakpoint();

  const accountTypeImagesCmsData = useSelector((state: AppState) => state.commerceProfile.accountTypeImagesCMS);
  const selectedAccountType: AccountType = useSelector((state: AppState) => state.commerceProfile.selectedAccountType);

  // To get record type from localstorage
  const localstorage = StorageService.getLocalStorage();
  const sharecodeAttributes: DynamicObjectInterface = localstorage?.retrieve?.(SHARECODE_ATTRIBUTES);

  const getRecordType = (shareAttributes?: DynamicObjectInterface): PersonalIdRequestTypeEnum => {
    if (!shareAttributes?.data) {
      return PersonalIdRequestTypeEnum.Cid;
    }

    try {
      const { recordType } = JSON.parse(shareAttributes.data);
      return recordType || PersonalIdRequestTypeEnum.Cid;
    } catch {
      return PersonalIdRequestTypeEnum.Cid;
    }
  };

  const recordType = getRecordType(sharecodeAttributes);

  const idVerificationImage = selectedAccountType
    ? accountTypeImagesCmsData?.[selectedAccountType]?.idVerificationImage
    : undefined;
  const idStepDesktopImage = idVerificationImage?.find((item: ImageReturnType) => item?.viewport.includes(breakpoint));
  const apiErrorHandler = useGlobalErrorHandler();

  const registrationData = useSelector(
    (state: AppState) => state.commerceProfile.registrationFlowData,
  ) as RegistrationFlowDataType;
  const { userData, OtpData, currentStepName } = registrationData;

  const getRegistration = async (regData: RegistrationFlowDataType) => {
    try {
      const tokenHeader = generateCustomHeaders(regData);
      const { userData } = regData;
      const { registrationId } = userData;
      const registrationRes = await apiErrorHandler(() =>
        profileApi.getRegistrationDetailsById(parseString(registrationId), tokenHeader),
      );

      return registrationRes;
    } catch (error) {
      // to return to caller for error handling
      return error;
    }
  };

  const handleNextButton = async (
    payload?: NextHandlerProps,
    slicePayload?: DynamicObjectInterface,
    stayOnSameStep?: boolean,
    callOnSuccess?: boolean,
  ) => {
    let verifyPayload: NextHandlerProps;
    let slicePayloadData: RegistrationFlowDataType = slicePayload;

    if (currentStepName === COAPP_STEPS.P2_ID_STEP && !payload?.consentAccepted) {
      // Phase 2
      const regRes = await getRegistration(slicePayload);

      const { versionNo, interimPhone } = parseObject(regRes);

      verifyPayload = {
        verifyPersonalId: payload?.spousePersonalId?.recordNumber,
        version: parseNumber(versionNo),
      };

      slicePayloadData = {
        ...slicePayload,
        userData: {
          ...slicePayload?.userData,
          patchVersion: versionNo,
        },
        perosonalDetails: {
          ...slicePayload?.perosonalDetails,
          interimPhone: parseString(interimPhone),
        },
      };
    } else if (!payload?.consentAccepted) {
      // Phase 1
      verifyPayload = {
        personalId: {
          recordNumber: payload?.spousePersonalId?.recordNumber,
          recordType: payload?.spousePersonalId?.recordType,
        },
        citizenship: payload?.spouseCitizenship,
        citizenshipCountry: payload?.spouseCitizenshipCountry,
        otpRequestId: OtpData.challengeId,
        version: parseNumber(userData.patchVersion),
      };
    }
    // If stayOnSameStep is true, then the API call will be made (Verify Button)
    // If stayOnSameStep is false, then the API call will be skipped (Next Button)
    const skipApiCall = stayOnSameStep ? APICallType.DEFAULT : APICallType.SKIP;

    // Seq #42 - id check
    return await nextHandler?.(verifyPayload, slicePayloadData, stayOnSameStep, callOnSuccess, skipApiCall);
  };

  // To reuse or copy the code from RegistrationIdVerificationStep
  return (
    <div className={styles['co-app-id-step-container']} id="co-app-id-step-container">
      <div className={styles['co-app-id-step-form']} data-testid="co-app-id-step-form">
        <RegistrationIdVerificationStep
          {...props}
          nextHandler={handleNextButton}
          verifySpouseOnly
          desktopImage={idStepDesktopImage}
          className={styles['desktop-container']}
          mobileImageClassName={styles['mobile-image-container']}
          recordType={recordType}
        />
      </div>
    </div>
  );
};
export default CoAppIdStep;
